export const normalizeRouteAppType = (value: unknown): string | null => {
  const normalized = String(value ?? '').trim();
  if (normalized === '1' || normalized === '2' || normalized === '3') {
    return normalized;
  }
  return null;
};

export const normalizeApplicationId = (value: unknown): string | null => {
  const normalized = String(value ?? '').trim();
  return normalized.length > 0 ? normalized : null;
};

export const parseRiskSummarySearchState = (
  search: string
): Record<string, unknown> | null => {
  const params = new URLSearchParams(search || '');
  const encodedData = params.get('data');
  if (!encodedData) {
    return null;
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(encodedData));
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : null;
  } catch (_error) {
    return null;
  }
};

export const mapApplicationTypeToRouteAppType = (value: unknown): string | null => {
  const explicit = normalizeRouteAppType(value);
  if (explicit) {
    return explicit;
  }

  const normalizedType = String(value || '')
    .trim()
    .toUpperCase();

  if (normalizedType === 'ATS' || normalizedType === 'APPLICATION') {
    return '1';
  }

  if (
    normalizedType === 'VR' ||
    normalizedType === 'DOC UPLOAD' ||
    normalizedType === 'DOCUPLOAD' ||
    normalizedType === 'DOC ANALYSIS'
  ) {
    return '2';
  }

  if (normalizedType === 'GUEST CARD' || normalizedType === 'GUESTCARD') {
    return '3';
  }

  return null;
};

type CurrentApplicationContextInput = {
  routeApplicationId?: unknown;
  routeAppType?: unknown;
  locationState?: Record<string, unknown> | null;
  locationSearch?: string;
};

export const resolveCurrentApplicationContext = ({
  routeApplicationId,
  routeAppType,
  locationState,
  locationSearch = '',
}: CurrentApplicationContextInput) => {
  const searchState = parseRiskSummarySearchState(locationSearch);
  const applicationId =
    normalizeApplicationId(routeApplicationId) ||
    normalizeApplicationId(locationState?.id) ||
    normalizeApplicationId(locationState?.appid) ||
    normalizeApplicationId(locationState?.AppID) ||
    normalizeApplicationId(locationState?.application_id) ||
    normalizeApplicationId(searchState?.id) ||
    normalizeApplicationId(searchState?.appid) ||
    normalizeApplicationId(searchState?.AppID) ||
    normalizeApplicationId(searchState?.application_id);

  const routeTypeCandidates = [
    routeAppType,
    locationState?.app_invitation_type,
    locationState?.appInvitationType,
    locationState?.appType,
    locationState?.applicationTypeId,
    locationState?.ApplicationType,
    locationState?.applicationType,
    searchState?.app_invitation_type,
    searchState?.appInvitationType,
    searchState?.appType,
    searchState?.applicationTypeId,
    searchState?.ApplicationType,
    searchState?.applicationType,
  ];

  let currentRouteAppType: string | null = null;
  for (const candidate of routeTypeCandidates) {
    const normalized = mapApplicationTypeToRouteAppType(candidate);
    if (normalized) {
      currentRouteAppType = normalized;
      break;
    }
  }

  return {
    currentApplicationId: applicationId,
    currentRouteAppType,
    searchState,
  };
};

type MatchedApplicationLike = {
  applicationId?: unknown;
  applicationType?: unknown;
  ApplicationType?: unknown;
  appInvitationType?: unknown;
  app_invitation_type?: unknown;
  appType?: unknown;
  applicationTypeId?: unknown;
};

export const resolveMatchedApplicationRouteAppType = (
  application: MatchedApplicationLike
): string | null => {
  const candidates = [
    application.appInvitationType,
    application.app_invitation_type,
    application.appType,
    application.applicationTypeId,
    application.applicationType,
    application.ApplicationType,
  ];

  for (const candidate of candidates) {
    const normalized = mapApplicationTypeToRouteAppType(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return null;
};

export const isCurrentApplicationMatch = (
  application: MatchedApplicationLike,
  currentContext: {
    currentApplicationId?: string | null;
    currentRouteAppType?: string | null;
  }
) => {
  const matchedApplicationId = normalizeApplicationId(application.applicationId);
  if (!matchedApplicationId || !currentContext.currentApplicationId) {
    return false;
  }

  if (matchedApplicationId !== currentContext.currentApplicationId) {
    return false;
  }

  if (!currentContext.currentRouteAppType) {
    return true;
  }

  const matchedRouteAppType = resolveMatchedApplicationRouteAppType(application);
  if (!matchedRouteAppType) {
    return true;
  }

  return matchedRouteAppType === currentContext.currentRouteAppType;
};

export const filterOutCurrentApplication = <T extends MatchedApplicationLike>(
  matchedApplications: T[] = [],
  currentContext: {
    currentApplicationId?: string | null;
    currentRouteAppType?: string | null;
  }
) =>
  matchedApplications.filter(
    (application) => !isCurrentApplicationMatch(application, currentContext)
  );

export const getVisibleTotalRecords = ({
  totalRecords,
  filteredLength,
  removedCount,
}: {
  totalRecords?: number | null;
  filteredLength: number;
  removedCount: number;
}) => {
  if (!Number.isFinite(totalRecords)) {
    return filteredLength;
  }

  return Math.max(Number(totalRecords) - removedCount, filteredLength, 0);
};
